import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';

import { AuthProvider, useAuth } from './context/AuthContext';
import IntakeForm from './components/IntakeForm';
import AdminLogin from './components/AdminLogin';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './components/AdminDashboard';
import Submissions from './components/Submissions';
import Events from './components/Events';
import KioskLauncher from './components/KioskLauncher';
import Settings from './components/Settings';
import BranchManagement from './components/BranchManagement';
import UserManagement from './components/UserManagement';
import LoadingScreen from './components/LoadingScreen';

axios.defaults.baseURL = '/';
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null, requiredPermission = null }) => {
  const { user, loading, isSuperAdmin, hasPermission } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check role requirement
  if (requiredRole) {
    if (requiredRole === 'SuperAdmin' && !isSuperAdmin()) {
      return <Navigate to="/admin/dashboard" />;
    }
  }

  // Check permission requirement
  if (requiredPermission && !isSuperAdmin()) {
    const [resource, action] = requiredPermission;
    if (!hasPermission(resource, action)) {
      return <Navigate to="/admin/dashboard" />;
    }
  }

  return children;
};

// Branch-specific route component
const BranchRoute = ({ children, branchCode }) => {
  const { user } = useAuth();
  
  // For public kiosk routes, we might need to verify branch exists
  // This is a placeholder for branch validation logic
  return children;
};

function AppContent() {
  const { loading } = useAuth();

  useEffect(() => {
    document.title = "Broker Union Platform";
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public kiosk form routes */}
        <Route path="/kiosk" element={<IntakeForm />} />
        <Route path="/kiosk/:branchCode" element={
          <BranchRoute branchCode={":branchCode"}>
            <IntakeForm />
          </BranchRoute>
        } />

        {/* Admin login */}
        <Route path="/login" element={<AdminLogin />} />

        {/* Redirect root to login */}
        <Route path="/" element={<Navigate to="/login" />} />

        {/* Admin protected routes */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="dashboard" />} />
          
          {/* Dashboard - Available to all authenticated users */}
          <Route path="dashboard" element={<AdminDashboard />} />
          
          {/* Submissions - Requires submissions read permission */}
          <Route 
            path="submissions" 
            element={
              <ProtectedRoute requiredPermission={['submissions', 'read']}>
                <Submissions />
              </ProtectedRoute>
            } 
          />
          
          {/* Events - Requires events read permission */}
          <Route 
            path="events" 
            element={
              <ProtectedRoute requiredPermission={['events', 'read']}>
                <Events />
              </ProtectedRoute>
            } 
          />
          
          {/* Kiosk Launcher - Requires events read permission */}
          <Route 
            path="kiosk" 
            element={
              <ProtectedRoute requiredPermission={['events', 'read']}>
                <KioskLauncher />
              </ProtectedRoute>
            } 
          />
          
          {/* Branch Management - SuperAdmin only */}
          <Route 
            path="branches" 
            element={
              <ProtectedRoute requiredRole="SuperAdmin">
                <BranchManagement />
              </ProtectedRoute>
            } 
          />
          
          {/* User Management - SuperAdmin or users read permission */}
          <Route 
            path="users" 
            element={
              <ProtectedRoute requiredPermission={['users', 'read']}>
                <UserManagement />
              </ProtectedRoute>
            } 
          />
          
          {/* Settings - Requires settings read permission */}
          <Route 
            path="settings" 
            element={
              <ProtectedRoute requiredPermission={['settings', 'read']}>
                <Settings />
              </ProtectedRoute>
            } 
          />
        </Route>

        {/* 404 Route */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </BrowserRouter>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
